import DropUploadIcon from '@src/svgs/DropUploadIcon';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';

import { usePageFileDrop } from './usePageFileDrop';

type Props = {
  /** Whether the page takes a dropped file right now. */
  enabled: boolean;
  onDrop: (files: File[]) => void;
  /** "Drop files here to upload", or where the file will go. */
  title: string;
  /** The formats and limit, on one line. */
  hint?: string;
};

/**
 * The page-level drop target (docs/design-system.md → Drop Zones → Page drop
 * overlay). Renders nothing until a file is dragged over the window; then a
 * full-viewport overlay names what happens on drop. The overlay never takes
 * pointer events, so the page's own drag handling underneath is unaffected.
 */
export default function PageDropZone({ enabled, onDrop, title, hint }: Props) {
  const { isDragging } = usePageFileDrop({ enabled, onDrop });
  if (!isDragging) return null;
  return createPortal(
    <Overlay data-testid="page-drop-zone" aria-hidden="true">
      <Frame />
      <Stack>
        <DropUploadIcon size="lg" />
        <Title>{title}</Title>
        {hint && <Hint>{hint}</Hint>}
      </Stack>
    </Overlay>,
    document.body,
  );
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  /* Above page chrome, below the shared Modal backdrop (1000010), so a dialog
     that opens on drop is never under it. */
  z-index: 1000009;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--overlay-dark);
  backdrop-filter: blur(2px);
  pointer-events: none;
  animation: ${fadeIn} 120ms ease-out;
`;

const Frame = styled.div`
  position: absolute;
  inset: var(--spacing-lg);
  border: 2px dashed color-mix(in srgb, var(--primary) 50%, transparent);
  border-radius: var(--radius-card);
`;

const Stack = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding: 0 var(--spacing-xl);
  text-align: center;
`;

const Title = styled.p`
  margin: 0;
  font-family: var(--font-primary);
  font-size: var(--text-lg);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

const Hint = styled.p`
  margin: 0;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;
